import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { FileDown } from 'lucide-react';
import { BASE_URL } from '../../services/base';

const ExportPoints = ({site_name}) => {
  const { theme } = useTheme();
  const { currentSiteId, token } = useAuth();
  const [loading, setLoading] = useState({
    stagnant: false,
    quality: false
  });
  const [loadingText, setLoadingText] = useState({
    stagnant: '',
    quality: ''
  });
  const [confirmOpen, setConfirmOpen] = useState({
    stagnant: false,
    quality: false
  });

  const loadingMessages = [
    'Preparing report...',
    'Analyzing data...',
    'Almost there...',
    'Hang on...',
    'Generating insights...'
  ];

  useEffect(() => {
    let interval;
    if (loading.stagnant || loading.quality) {
      let messageIndex = 0;
      interval = setInterval(() => {
        messageIndex = (messageIndex + 1) % loadingMessages.length;
        setLoadingText(prev => ({
          ...prev,
          stagnant: loading.stagnant ? loadingMessages[messageIndex] : '',
          quality: loading.quality ? loadingMessages[messageIndex] : ''
        }));
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [loading.stagnant, loading.quality]);

  const handleInitialClick = (type) => {
    const key = type === 'Stagnant' ? 'stagnant' : 'quality';
    setConfirmOpen(prev => ({
      ...prev,
      [key]: true
    }));

    // Auto-close confirmation after 3 seconds
    setTimeout(() => {
      setConfirmOpen(prev => ({
        ...prev,
        [key]: false
      }));
    }, 3000);
  };

  const downloadReport = async (type) => {
    const loadingKey = type === 'Stagnant' ? 'stagnant' : 'quality';
    setConfirmOpen(prev => ({
      ...prev,
      [loadingKey]: false
    }));
    setLoading(prev => ({ ...prev, [loadingKey]: true }));
    setLoadingText(prev => ({ ...prev, [loadingKey]: 'Preparing report...' }));

    try {
      const response = await fetch(
        `${BASE_URL}/nodes/${currentSiteId}/points/report?chart_type=${encodeURIComponent(type)}&site_name=${encodeURIComponent(site_name)}&timezone=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Get filename from Content-Disposition header if present
      const contentDisposition = response.headers.get('Content-Disposition');
      
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+\.(pdf|zip))"/);
      const filename = filenameMatch ? filenameMatch[1] : `${type.toLowerCase()}_report.pdf`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

    } catch (error) {
      console.error('Error downloading report:', error);
    } finally {
      setLoading(prev => ({ ...prev, [loadingKey]: false }));
      setLoadingText(prev => ({ ...prev, [loadingKey]: '' }));
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-col gap-2">
        <button
          onClick={() => confirmOpen.stagnant ? downloadReport('Stagnant') : handleInitialClick('Stagnant')}
          disabled={loading.stagnant}
          className={`relative px-3 py-2 text-sm rounded-lg border backdrop-blur-sm flex items-center justify-center gap-2 transition-all duration-200 flex-1 ${
            'text-yellow-600'
          } ${
            theme === 'light'
              ? 'border-gray-300/50 hover:bg-gray-50/50 hover:border-gray-400/50'
              : 'border-gray-700/50 hover:bg-gray-800/50 hover:border-gray-600/50'
          }`}
        >
          <FileDown className="w-4 h-4" />
          {loading.stagnant ? loadingText.stagnant : (confirmOpen.stagnant ? 'Click to confirm' : (
            <span>
              <span className="hidden lg:inline">Download Stagnant Data Analysis Report</span>
              <span className="lg:hidden">Stagnant Data Report</span>
            </span>
          ))}
          {loading.stagnant && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/5 backdrop-blur-sm rounded-lg">
              <div className="relative w-6 h-6">
                <div className="absolute inset-0 border-2 border-yellow-500/20 rounded-full"></div>
                <div className="absolute inset-0 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                <div className="absolute inset-1 border border-yellow-400/20 rounded-full"></div>
                <div className="absolute inset-2 border border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </div>
          )}
        </button>

        <button
          onClick={() => confirmOpen.quality ? downloadReport('Low quality') : handleInitialClick('Low quality')}
          disabled={loading.quality}
          className={`relative px-3 py-2 text-sm rounded-lg border backdrop-blur-sm flex items-center justify-center gap-2 transition-all duration-200 flex-1 ${
            'text-pink-600'
          } ${
            theme === 'light'
              ? 'border-gray-300/50 hover:bg-gray-50/50 hover:border-gray-400/50'
              : 'border-gray-700/50 hover:bg-gray-800/50 hover:border-gray-600/50'
          }`}
        >
          <FileDown className="w-4 h-4" />
          {loading.quality ? loadingText.quality : (confirmOpen.quality ? 'Click to confirm' : (
            <span>
              <span className="hidden lg:inline">Download Data Quality Analysis Report</span>
              <span className="lg:hidden">Data Quality Report</span>
            </span>
          ))}
          {loading.quality && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/5 backdrop-blur-sm rounded-lg">
              <div className="relative w-6 h-6">
                <div className="absolute inset-0 border-2 border-pink-500/20 rounded-full"></div>
                <div className="absolute inset-0 border-2 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
                <div className="absolute inset-1 border border-pink-400/20 rounded-full"></div>
                <div className="absolute inset-2 border border-pink-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

export default ExportPoints;
